import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useMeetingFromParams, { 
    saveParticipantInfo, 
    clearParticipantInfo,
    getStoredParticipantInfo 
} from "../utils/useMeetingFromParams";
import { defaultUser, useUserStore } from "../../stores/useUserStore";
import { useMeetingStore } from "../../stores/useMeetingStore";
import useTimeoutEffect from "../utils/useTimeoutEffect";
import useLeaveMeetingPage from "../utils/useLeaveMeetingPage";
import useLeaveMeeting from "../api/useLeaveMeeting";
import IParticipant from "../../api/IParticipant";
import { toString } from "../../utilities/stringify";
import useLogin from "../api/useLogin";
import useIsOpen from "../utils/useIsOpen";
import useAxios from "../api/useAxios";
import useShowError from "../utils/useShowError";
import useAllDecks from "../api/useAllDecks";
import IMeeting from "../../api/IMeeting";
import IParticipantPoker from "../../api/IParticipantPoker";
import guid from "../../utilities/guid";
import { SelectItem } from "../../components/inputs/SelectInput";
import useRoundControl from "../api/useRoundControl";
import ISubTopic from "../../api/ISubTopic";
import { findMeetingTopic } from "../../utilities/findTopic";
import { DeckIds } from "../../api/IDeck";
import IPoker from "../../api/IPoker";
import useComponentDimensions from "../utils/useComponentDimensions";

type ParticipantPageParams = {
    id: string;
};

const useParticipantPage = () => {
    const navigate = useNavigate();
    const { id: meetingId } = useParams<ParticipantPageParams>();
    const routeState = useLocation().state as { name: string };

    const { ref, height: maxHeight } = useComponentDimensions();

    const meeting = useMeetingStore((state) => state.meeting);
    const user = useUserStore((state) => state.user);

    /**
     * 从路由状态或localStorage获取参与者名称
     */
    const getParticipantName = useCallback((): string | undefined => {
        // 优先使用路由传递的名称
        if (routeState?.name) {
            return routeState.name;
        }
        // 其次使用localStorage中保存的信息
        if (meetingId) {
            const storedInfo = getStoredParticipantInfo(meetingId);
            return storedInfo?.name;
        }
        return undefined;
    }, [routeState?.name, meetingId]);

    const participantName = getParticipantName();

    const [participant, setParticipant] = useState<IParticipant | undefined>(
        undefined
    );

    const [selected, setSelected] = useState("");
    const select = (value: string) =>
        setSelected((prev) => (prev === value ? "" : value));

    const [confirmModalOpen, openConfirmModal, closeConfrimModal] =
        useIsOpen(false);
    const [confirmed, confirm, unConfirm] = useIsOpen(false);

    const meetingLoading = useMeetingFromParams();

    const leavePage = useLeaveMeetingPage();

    const { leaveMeeting } = useLeaveMeeting();

    const { error, loading, setError, clearError, post } = useAxios();
    const { decks, loading: decksLoading } = useAllDecks({
        axiosProps: { error, setError },
    });
    const changeDeck = async (val: string) => {
        if (!meeting) return setError(`Missing meeting`);

        await post<any>(
            `/api/meeting/UpdateParticipantDeck?meetingId=${meeting.id}&userName=${participantName}&deckId=${val}`
        );
        if (!confirmed) setSelected("");
    };
    useShowError(error, clearError);

    const deckOptions = decks.map(
        (deck): SelectItem => ({
            key: deck.id,
            value: deck.id,
            placeHolder: deck.description,
        })
    );

    const { roundStarted: isVoting, isShowingResults } = useRoundControl();

    /**
     * 当参与者信息确定后，将其保存到localStorage以便刷新时恢复
     */
    useEffect(() => {
        if (meetingId && participantName) {
            // 如果还没有存储过分组信息，从现有数据中提取（可以从参与者信息或默认值）
            const storedInfo = getStoredParticipantInfo(meetingId);
            const participantInMeeting = (meeting?.participants ?? []).find(
                (p) => p.name === participantName
            );
            
            const group = storedInfo?.group || participantInMeeting?.group || "";
            
            // 保存参与者信息
            saveParticipantInfo(meetingId, participantName, group);
        }
    }, [meetingId, participantName, meeting?.participants]);

    /**
     * 监听会议中的参与者更新
     */
    useEffect(() => {
        if (meetingLoading || !participantName || !meeting) return;

        const found = (meeting.participants ?? []).find(
            (person) => person.name === participantName
        );

        if (!found) {
            console.warn(`Participant ${participantName} not found in meeting`);
            return;
        }

        // 更新本地参与者状态
        if (toString(participant) !== toString(found)) {
            console.log("Updating participant data:", found);
            setParticipant(found);

            // 如果已选择扑克，保存选择
            if (found.selectedPoker) {
                setSelected(found.selectedPoker.pokerValue);
                confirm();
            }
        }
    }, [toString(meeting), meetingLoading, participantName]);

    /**
     * 定期检查参与者是否仍在会议中
     */
    useTimeoutEffect(
        () => {
            if (meetingLoading) return;

            // 如果没有参与者名称，等待数据加载
            if (!participantName) {
                console.warn("Participant name not found, waiting for state to load...");
                return;
            }

            let found = (meeting?.participants ?? ([] as IParticipant[])).find(
                (person) => person.name === participantName
            );

            if (!found) {
                console.warn(`Participant ${participantName} not found in meeting, leaving page...`);
                return onArrowBack();
            }
        },
        [
            toString(meeting),
            meetingLoading,
            participantName,
            user.id,
        ],
        1500  // 1.5秒检查一次
    );

    /**
     * 投票结束且不再显示结果时清除选择
     */
    useEffect(() => {
        if (!isVoting && !isShowingResults && confirmed) {
            console.log("Voting ended and results hidden, clearing poker selection");
            unConfirm();
            setSelected("");
        }
    }, [isVoting, isShowingResults]);

    const getVotingTopic = useCallback(
        (subTopics: ISubTopic[], topicId?: string) => {
            if (!topicId) return undefined;
            return findMeetingTopic(topicId, subTopics);
        },
        []
    );

    /**
     * 退出时标记为离线（保留投票状态），host 可见其已退出，重新进入时恢复退出前状态
     */
    const onArrowBack = async () => {
        if (participantName) {
            await leaveMeeting(participantName);
        }
        // 清除localStorage中保存的参与者信息（主动离开）
        if (meetingId) {
            clearParticipantInfo(meetingId);
        }
        leavePage();
    };

    const onConfirm = async () => {
        const findOriginalPoker = (): IPoker | undefined =>
            participant?.deckId === DeckIds.Custom
                ? {
                      id: guid.create(),
                      value: selected,
                  }
                : decks
                      .find((deck) => deck.id === participant?.deckId)
                      ?.pokers.find((poker) => poker.value === selected);

        const isValid = () => {
            if (!meeting) return setError(`Missing meeting`);

            let originalPoker = findOriginalPoker();

            if (!originalPoker) return setError(`Missing selected poker`);

            return true;
        };

        if (!isValid()) return;

        const constructParticipantPoker = () => {
            const originalPoker = findOriginalPoker()!;

            let selectedPoker: IParticipantPoker = {
                id: guid.create(),
                pokerId: originalPoker.id,
                pokerValue: originalPoker.value,
                originalPokerValue: originalPoker.value,
                participantName: participantName!,
            };

            return selectedPoker;
        };

        console.log("Submitting poker selection:", selected);

        let res = await post<IMeeting>(
            `/api/meeting/SelectPokers?meetingId=${meeting!.id}`,
            {
                meetingId: meeting!.id,
                userName: participantName,
                selectedPoker: constructParticipantPoker(),
            }
        );

        if (!res) {
            console.error("Failed to submit poker selection");
            return;
        }

        console.log("Poker selection submitted successfully");
        confirm();
        closeConfrimModal();
    };

    return {
        ref,
        maxHeight,
        loading,
        isVoting,
        isShowingResults,
        decksLoading,
        participant,
        selected,
        confirmed,
        confirmModalOpen,
        deckOptions,
        votingTopic: getVotingTopic(
            meeting?.subTopics ?? [],
            meeting?.votingOn
        ),

        openConfirmModal,
        closeConfrimModal,
        onArrowBack,
        select,
        changeDeck,
        confirm: onConfirm,
        unConfirm: () => {
            unConfirm();
            closeConfrimModal();
        },
    };
};

export default useParticipantPage;
